import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';

import { Resolvers } from '../../types/Resolvers';
import { getDataFromResolver } from '../../services/resolvers';
import { ModeloVehiculo } from '../../types/vehicle.types';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import { SmartCapitalizePipe } from '../../utils/pipes/smart-capitalize.pipe';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { toSlug } from '../../utils/slugs/slug.utils';

interface VehicleModel {
  name: string;
  href: string;
}

interface BrandModels {
  brand: string;
  models: VehicleModel[];
  showAll?: boolean;
}

@Component({
  selector: 'allas-comprar-por-modelo',
  imports: [
    MainLayout,
    FormsModule,
    CommonModule,
    SmartCapitalizePipe,
    MarginWrapper,
  ],
  template: `
    <allas-main-layout [showPopularSections]="false" [showNewsletter]="false">
      <allas-margin-wrapper styleClass="bg-white fs-condensed">
        <!-- Header Section -->
        <nav
          class="py-8 lg:py-12"
          role="navigation"
          aria-label="Navegación principal"
        >
          <div class="container mx-auto px-4 lg:px-8">
            <div class="max-w-4xl mx-auto text-center">
              <h1
                class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 font-inter"
              >
                COMPRAR POR MODELO
              </h1>
              <p class="text-lg text-gray-600 mb-8 font-inter">
                ALLAS REPUESTOS tiene una amplia selección de repuestos para
                cada modelo en esta lista.
              </p>

              <!-- Search Bar -->
              <div class="max-w-md mx-auto">
                <label for="model-search" class="sr-only">
                  Buscar modelo específico
                </label>
                <div class="relative">
                  <input
                    id="model-search"
                    type="text"
                    [ngModel]="searchTerm()"
                    (ngModelChange)="searchTerm.set($event)"
                    placeholder="Buscar modelo específico..."
                    class="w-full px-4 py-3 pl-12 pr-4 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all duration-200 placeholder:text-gray-500"
                    aria-describedby="search-description"
                  />
                  <svg
                    class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    ></path>
                  </svg>
                </div>
                <div id="search-description" class="sr-only">
                  Busca modelos específicos de vehículos para encontrar
                  repuestos compatibles
                </div>
              </div>
            </div>
          </div>
        </nav>

        <main
          class="container mx-auto px-4 lg:px-8 py-8 lg:py-12"
          role="main"
          aria-labelledby="models-heading"
        >
          <!-- Models by Brand Grid -->
          <section aria-labelledby="models-heading">
            <h2 id="models-heading" class="sr-only">
              Modelos de vehículos por marca
            </h2>
            <div
              class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            >
              @for (brand of filteredModels(); track brand.brand) {
              <ng-container
                [ngTemplateOutlet]="brandCard"
                [ngTemplateOutletContext]="{ brand: brand }"
              ></ng-container>
              }
            </div>
          </section>

          <!-- No Results -->
          @if (searchTerm() && filteredModels().length === 0) {
          <ng-container [ngTemplateOutlet]="noResults"></ng-container>
          }

          <!-- Additional Info -->
          @if (searchTerm() && filteredModels().length === 0) {
          <ng-container [ngTemplateOutlet]="additionalInfo"></ng-container>
          }
        </main>
      </allas-margin-wrapper>

      <!-- Brand Card Template -->
      <ng-template #brandCard let-brand="brand">
        <article class="bg-white">
          <h3
            class="text-lg font-bold text-gray-900 mb-4 font-inter border-b border-gray-200 pb-2"
          >
            {{ brand.brand }}
          </h3>
          <ul class="space-y-2" role="list">
            @for (model of getModelsToShow(brand); track model.name) {
            <li>
              <ng-container
                [ngTemplateOutlet]="modelLink"
                [ngTemplateOutletContext]="{ model: model }"
              ></ng-container>
            </li>
            } @if (brand.models.length > 5) {
            <li class="pt-2">
              <ng-container
                [ngTemplateOutlet]="expandButton"
                [ngTemplateOutletContext]="{ brand: brand }"
              ></ng-container>
            </li>
            }
          </ul>
        </article>
      </ng-template>

      <!-- Model Link Template -->
      <ng-template #modelLink let-model="model">
        <a
          [href]="model.href"
          class="group flex items-center justify-between text-sm text-gray-600 hover:text-red-600 transition-colors font-inter py-1"
          [attr.aria-label]="
            'Ver repuestos para ' + getModelDisplayName(model.name)
          "
        >
          <span class="flex items-center gap-2">
            Repuestos para
            {{ getModelDisplayName(model.name) | smartCapitalize }}
          </span>
          <svg
            class="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
        </a>
      </ng-template>

      <!-- Expand Button Template -->
      <ng-template #expandButton let-brand="brand">
        <button
          (click)="toggleBrandExpansion(brand.brand)"
          class="text-sm font-medium text-red-600 hover:text-red-700 transition-colors font-inter flex items-center gap-1 ml-5"
          [attr.aria-expanded]="expandedBrands().has(brand.brand)"
          [attr.aria-label]="
            (expandedBrands().has(brand.brand) ? 'Ocultar' : 'Mostrar') +
            ' todos los modelos de ' +
            brand.brand
          "
        >
          {{ expandedBrands().has(brand.brand) ? 'VER MENOS' : 'VER TODOS' }}
          <svg
            class="w-3.5 h-3.5 transition-transform"
            [class.rotate-90]="expandedBrands().has(brand.brand)"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
        </button>
      </ng-template>

      <!-- No Results Template -->
      <ng-template #noResults>
        <section class="text-center py-12" aria-labelledby="no-results-heading">
          <div class="text-gray-400 mb-4">
            <svg
              class="mx-auto w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <h3
            id="no-results-heading"
            class="text-xl font-semibold text-gray-900 mb-2 font-inter"
          >
            No se encontraron modelos
          </h3>
          <p class="text-gray-600 mb-6 font-inter">
            No encontramos modelos que coincidan con "{{ searchTerm() }}"
          </p>
          <div class="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
            <h4 class="font-semibold text-gray-900 mb-2 font-inter">
              ¿No encuentras tu modelo?
            </h4>
            <p class="text-sm text-gray-600 mb-4 font-inter">
              Contáctanos y te ayudamos a encontrar los repuestos que necesitas
            </p>
            <div class="space-y-2 text-sm">
              <div class="flex items-center gap-2 text-gray-600">
                <span class="font-medium">WhatsApp:</span>
                <a
                  href="https://wa.me/50495089953"
                  class="text-red-600 hover:text-red-700 transition-colors"
                >
                  +504 9508-9953
                </a>
              </div>
              <div class="flex items-center gap-2 text-gray-600">
                <span class="font-medium">Email:</span>
                <a
                  href="mailto:info@allasrepuestos.hn"
                  class="text-red-600 hover:text-red-700 transition-colors"
                >
                  info&#64;allasrepuestos.hn
                </a>
              </div>
            </div>
          </div>
        </section>
      </ng-template>

      <!-- Additional Info Template -->
      <ng-template #additionalInfo>
        <section
          class="mt-12 bg-gray-50 rounded-lg p-6 lg:p-8"
          aria-labelledby="benefits-heading"
        >
          <h3
            id="benefits-heading"
            class="text-xl font-bold text-gray-900 mb-4 font-inter"
          >
            ¿Por qué comprar repuestos por modelo específico?
          </h3>
          <div
            class="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600 font-inter"
          >
            <article>
              <h4 class="font-semibold text-gray-900 mb-2">
                Compatibilidad Garantizada
              </h4>
              <p>
                Todos los repuestos están verificados para tu modelo específico
                de vehículo.
              </p>
            </article>
            <article>
              <h4 class="font-semibold text-gray-900 mb-2">
                Instalación Perfecta
              </h4>
              <p>
                Las piezas encajan perfectamente sin necesidad de
                modificaciones.
              </p>
            </article>
            <article>
              <h4 class="font-semibold text-gray-900 mb-2">Calidad Original</h4>
              <p>
                Repuestos originales y de alta calidad para mantener el
                rendimiento.
              </p>
            </article>
            <article>
              <h4 class="font-semibold text-gray-900 mb-2">
                Asesoría Especializada
              </h4>
              <p>
                Nuestros expertos conocen las especificaciones de cada modelo.
              </p>
            </article>
          </div>
        </section>
      </ng-template>
    </allas-main-layout>
  `,
})
export class ComprarPorModelo implements OnInit {
  private route = inject(ActivatedRoute);
  private title = inject(Title);
  private meta = inject(Meta);

  smartTitle = smartTitle;

  searchTerm = signal('');
  expandedBrands = signal<Set<string>>(new Set());

  modelosFromResolver =
    getDataFromResolver<ModeloVehiculo[]>(
      Resolvers.ALL_VEHICLE_MODELS,
      this.route
    ) || [];

  vehicleModels: BrandModels[] = [];

  constructor() {
    this.populateModelsFromVehicles();
  }

  ngOnInit() {
    this.setMetaTags();
  }

  private setMetaTags() {
    const pageTitle =
      'Comprar Repuestos por Modelo de Vehículo | Allas Repuestos';
    const description =
      'Encuentra repuestos específicos para tu modelo de vehículo. Amplia selección con envío a domicilio en Honduras.';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  private populateModelsFromVehicles(): void {
    // Group models by brand from modelosFromResolver
    const brandGroups: Record<string, string[]> = {};

    this.modelosFromResolver.forEach((vehicle) => {
      if (!brandGroups[vehicle.marca]) {
        brandGroups[vehicle.marca] = [];
      }
      if (!brandGroups[vehicle.marca].includes(vehicle.modelo)) {
        brandGroups[vehicle.marca].push(vehicle.modelo);
      }
    });

    // Convert to BrandModels format
    this.vehicleModels = Object.entries(brandGroups).map(([brand, models]) => ({
      brand: brand.toUpperCase(),
      models: models.map((modelo) => ({
        name: `${brand} ${modelo} Repuestos`,
        href: this.getModelUrl(brand, modelo),
        popular: true,
      })),
      showAll: true,
    }));
  }

  private getModelUrl(brand: string, modelo: string): string {
    // Vehículo popular - usar ruta específica
    return `/repuestos/v/${toSlug(brand)}/${toSlug(modelo)}`;
  }

  // Filter models based on search term
  filteredModels = computed(() => {
    if (!this.searchTerm()) return this.vehicleModels;

    return this.vehicleModels
      .map((brand) => ({
        ...brand,
        models: brand.models.filter((model) =>
          model.name.toLowerCase().includes(this.searchTerm().toLowerCase())
        ),
      }))
      .filter((brand) => brand.models.length > 0);
  });

  toggleBrandExpansion(brandName: string) {
    this.expandedBrands.update((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(brandName)) {
        newSet.delete(brandName);
      } else {
        newSet.add(brandName);
      }
      return newSet;
    });
  }

  getModelsToShow(brand: BrandModels): VehicleModel[] {
    const showAll = this.expandedBrands().has(brand.brand);
    return showAll ? brand.models : brand.models.slice(0, 5);
  }

  getModelDisplayName(modelName: string): string {
    return modelName.replace(' Repuestos', '');
  }
}
